import React, { useState, useEffect, useReducer, useRef, useContext } from "react";

import { isSameDay, parseISO, format } from "date-fns";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";
import InteractiveMessageView from "../InteractiveMessageView";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    height: "100%", // Forçar altura total
  },

  messagesList: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px",
    overflowY: "auto !important", // Garantir scroll
    height: "100%",
    backgroundImage: "url('/chat-background.png')",
    backgroundRepeat: "repeat",
    backgroundSize: "auto",
    backgroundPosition: "center",
    [theme.breakpoints.down("sm")]: {
      paddingBottom: "90px",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "#ffffff",
    color: "#303030",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: "#f0f0f0",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "#dcf8c6",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: "#cfe9ba",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "absolute",
    color: "#999",
    zIndex: 1,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    opacity: "90%",
    padding: "4px",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "rgba(255, 255, 255, 1)" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },

  chatMessageContent: {
    "&:hover #messageActionsButton": {
      display: "flex !important",
    },
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "DELETE_MESSAGE") {
    const messageId = action.payload.id;
    return state.filter((m) => m.id !== messageId);
  }

  if (action.type === "RESET") {
    return [];
  }
};

import { AuthContext } from "../../context/Auth/AuthContext";

const MessagesList = ({ ticketId, isGroup, isSpy }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber, read: isSpy ? "false" : "true" },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("appMessage", (data) => {
      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_MESSAGE", payload: data.message });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    else if (message.mediaType === "vcard") {
      //console.log("vcard")
      //console.log(message)
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />
    }
    /*else if (message.mediaType === "multi_vcard") {
      console.log("multi_vcard")
      console.log(message)
    	
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
    else if (/^.*\.(jpe?g|png|gif)?$/i.exec(message.mediaUrl) && message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (message.mediaType === "audio") {
      return <Audio url={message.mediaUrl} />
    } else if (message.mediaType === "video") {
      return (
        <video
          className={classes.messageMedia}
          src={message.mediaUrl}
          controls
        />
      );
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              color="primary"
              variant="outlined"
              target="_blank"
              href={message.mediaUrl}
            >
              Download
            </Button>
          </div>
          <Divider />
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    }
    if (index === messagesList.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}
          {message.quotedMsg?.body}
        </div>
      </div>
    );
  };

  const renderMessageBody = (message) => {
    if (message.body && typeof message.body === "string" && message.body.trim().startsWith("{")) {
      try {
        const jsonBody = JSON.parse(message.body);

        // Se for InteractiveMessage, usar o componente dedicado
        if (jsonBody.InteractiveMessage || jsonBody.interactiveMessage || (jsonBody.body && jsonBody.header)) {
          return <InteractiveMessageView msg={message} />;
        }

        // Tratar Enquetes (Poll Creation)
        if (jsonBody.pollCreationMessage || jsonBody.type === "poll") {
          const question = jsonBody.pollCreationMessage?.name || jsonBody.text || "Enquete";
          const options = jsonBody.pollCreationMessage?.options || jsonBody.choices || [];
          const pollId = message.id; // ID interno da mensagem de enquete

          // Calcular votos
          let totalVotes = 0;
          const voteCounts = {};

          messagesList.forEach((msg) => {
            if (msg.body && typeof msg.body === "string" && msg.body.startsWith("{")) {
              try {
                const body = JSON.parse(msg.body);
                // Verificar se é um voto para esta enquete
                if (body.pollCreationMessageKey && body.pollCreationMessageKey.id === pollId) {
                  // Tentar identificar a opção votada
                  // Nota: O WhatsApp Web normal envia o hash. Se não tivermos hash sync,
                  // contaremos apenas o total ou se o body tiver o nome (alguns mods têm).
                  totalVotes++;

                  // Se houver lógica de opção selecionada futuramente:
                  // const selected = body.vote?.selectedOptions?.[0]; // Hash
                  // if (selected) voteCounts[selected] = (voteCounts[selected] || 0) + 1;
                }
              } catch (e) { }
            }
          });

          return (
            <div style={{ padding: 10, backgroundColor: "#e6f7ff", borderRadius: 5, minWidth: 250 }}>
              <div style={{ fontWeight: "bold", display: "flex", alignItems: "center", marginBottom: 8, color: "#0078d4" }}>
                <span style={{ fontSize: 20, marginRight: 8 }}>📊</span>
                Enquete
              </div>
              <div style={{ marginBottom: 8, fontSize: "1.1rem", fontWeight: "500" }}>{question}</div>
              {options.length > 0 && (
                <ul style={{ paddingLeft: 20, margin: 0, listStyle: "none" }}>
                  {options.map((opt, idx) => {
                    const optText = typeof opt === 'string' ? opt : opt.optionName;
                    // Placeholder para contagem individual se conseguirmos o hash
                    // const count = voteCounts[hash] || 0;
                    return (
                      <li key={idx} style={{ marginBottom: 4, color: "#555", display: "flex", justifyContent: "space-between" }}>
                        <span>• {optText}</span>
                        {/* <span style={{fontWeight: "bold"}}>{count}</span> */}
                      </li>
                    );
                  })}
                </ul>
              )}
              <div style={{ marginTop: 10, borderTop: "1px solid #cceeff", paddingTop: 5, fontSize: "0.85rem", color: "#005a9e", textAlign: "right" }}>
                Total de Votos: <strong>{totalVotes}</strong>
                <br />
                <small style={{ fontSize: "0.7rem", color: "#999" }}>
                  ID: {pollId} <br />
                  Votos encontrados: {totalVotes}
                </small>
              </div>
            </div>
          );
        }

        // Tratar Votos (Poll Vote) - Esconder visualmente detalhado, pois já contamos na enquete
        if (jsonBody.pollCreationMessageKey && jsonBody.vote) {
          return (
            <div style={{ padding: 8, backgroundColor: "#f0f2f5", borderRadius: 5, display: "flex", alignItems: "center", border: "1px solid #e1e4e8", opacity: 0.8 }}>
              <span style={{ fontSize: 16, marginRight: 8 }}>🗳️</span>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: "0.8rem", color: "#666" }}>Voto contabilizado</span>
              </div>
            </div>
          );
        }

        if (jsonBody.singleSelectReply && jsonBody.singleSelectReply.selectedRowID) {
          return jsonBody.singleSelectReply.selectedRowID;
        }
        if (jsonBody.title) {
          return jsonBody.title;
        }
      } catch (e) {
        // Not JSON
      }
    }
    return message.body;
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div className="chat-message-left mb-4 d-flex align-items-start">
                {message.contact?.profilePicUrl ? (
                  <img
                    src={message.contact.profilePicUrl}
                    className="rounded-circle avatar-xs me-2 mt-1"
                    width="30" height="30"
                    alt=""
                  />
                ) : (
                  <div className="rounded-circle avatar-xs avatar-letter me-2 mt-1">
                    {message.contact?.name?.charAt(0).toUpperCase() || "?"}
                  </div>
                )}
                <div className={`chat-message-content bg-white p-3 rounded-3 shadow-sm position-relative ${classes.chatMessageContent}`} style={{ maxWidth: '75%' }}>
                  <IconButton
                    variant="contained"
                    size="small"
                    id="messageActionsButton"
                    disabled={message.isDeleted && user.profile !== "admin"}
                    className={classes.messageActionsButton}
                    onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                    style={{ position: 'absolute', top: '5px', right: '5px' }}
                  >
                    <i className="fi fi-rr-angle-small-down"></i>
                  </IconButton>

                  {isGroup && (
                    <span className="fw-bold d-block mb-1" style={{ fontSize: '0.8rem', color: '#555' }}>
                      {message.contact?.name || message.contact?.pushname || message.contact?.number}
                    </span>
                  )}

                  <div className="message-media-container">
                    {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard") && checkMessageMedia(message)}
                  </div>

                  <div className="message-text" style={{ whiteSpace: "pre-wrap" }}>
                    {message.quotedMsg && renderQuotedMessage(message)}
                    <MarkdownWrapper>{renderMessageBody(message)}</MarkdownWrapper>
                  </div>

                  <small className="text-muted d-block mt-2" style={{ fontSize: '0.7rem' }}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </small>
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div className="chat-message-right mb-4 d-flex align-items-end justify-content-end">
                <div className={`chat-message-content p-3 rounded-3 shadow-sm position-relative ${classes.chatMessageContent}`} style={{ maxWidth: '75%', backgroundColor: '#d9fdd3', color: '#000' }}>
                  <IconButton
                    variant="contained"
                    size="small"
                    id="messageActionsButton"
                    disabled={message.isDeleted && user.profile !== "admin"}
                    className={classes.messageActionsButton}
                    onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                    style={{ position: 'absolute', top: '5px', left: '5px', color: 'rgba(0,0,0,0.7)' }}
                  >
                    <i className="fi fi-rr-angle-small-down"></i>
                  </IconButton>

                  <div className="message-media-container text-dark">
                    {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard") && checkMessageMedia(message)}
                  </div>

                  <div className={`message-text ${message.isDeleted ? 'opacity-50 fst-italic' : ''}`} style={{ whiteSpace: "pre-wrap" }}>
                    {message.isDeleted && <i className="fi fi-rr-ban me-1"></i>}
                    {message.quotedMsg && renderQuotedMessage(message)}
                    <MarkdownWrapper>{renderMessageBody(message)}</MarkdownWrapper>
                  </div>

                  <div className="d-flex align-items-center justify-content-end mt-2">
                    <small style={{ fontSize: '0.7rem', color: '#525252' }}>
                      {format(parseISO(message.createdAt), "HH:mm")}
                    </small>
                    <span className="ms-1 d-flex align-items-center" style={{ fontSize: '14px' }}>
                      {renderMessageAck(message)}
                    </span>
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Say hello to your new contact!</div>;
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : []}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;